import { useEffect, useCallback, useMemo } from 'react';
import { atom, useRecoilState } from 'recoil';
import Mousetrap from 'mousetrap';

// @ts-expect-error [EN-10263] - TS7016 - Could not find a declaration file for module 'gamepad.js'. '/Users/danyim/Dev/services-frontend-experiment/frontend/node_modules/gamepad.js/gamepad.js' implicitly has an 'any' type.
import { GamepadListener } from 'gamepad.js';
import { DictaphoneKey, DictaphonePreferences, GamepadActionId } from 'generated/graphql';
import { useToolPreferences } from 'hooks/usePreferences';
import { useMicrophone } from 'domains/reporter/useMicrophone';
import { WithApolloContext } from 'modules/Apollo/types';
import {
  isVirtualDictaphoneEnabled,
  VIRTUAL_DICTAPHONE_DEADMAN_SWITCH_KEYCODE,
  VIRTUAL_DICTAPHONE_DEFAULT_KEY_MAPPING,
} from './dictaphone/products/virtual';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { useDictaphoneKeyMapping } from 'utils/dictaphone/useDictaphoneKeyMapping';
import { DictaphoneKeyMapping } from './dictaphone/products/types';

const USE_VIRTUAL_GAMEPAD = isVirtualDictaphoneEnabled();

// The following code is a way to let developers without a dictaphone
// test the dictaphone-related functionalities
class VirtualGamepad {
  constructor() {
    // By default, Mousetrap disables shortcuts while the user is typing in a text input
    // we need to override its default behavior to allow the Virtual Gamepad shortcuts to
    // work while the developer is typing inside a text editor.
    const { stopCallback } = Mousetrap.prototype;
    Mousetrap.prototype.stopCallback = function (e: any, element: any, combo: any) {
      if (['ctrl+1', 'ctrl+2', 'ctrl+3', 'ctrl+4', 'ctrl+5', 'ctrl+6'].includes(combo)) {
        return false;
      }
      return stopCallback.call(e, e, element, combo);
    };
    Mousetrap.bind('ctrl+1', (evt) => this.press(0));
    Mousetrap.bind('ctrl+2', (evt) => this.press(5));
    Mousetrap.bind('ctrl+3', (evt) => this.press(2));
    Mousetrap.bind('ctrl+4', (evt) => this.press(14));
    Mousetrap.bind('ctrl+5', (evt) => this.press(7));
    Mousetrap.bind('ctrl+6', (evt) =>
      this.press(Number(VIRTUAL_DICTAPHONE_DEADMAN_SWITCH_KEYCODE))
    );
  }

  press(keyCode: number) {
    window.__SIRONA_GAMEPAD_ADAPTER__.emit('gamepad:button', {
      pressed: true,
      button: keyCode,
    });
  }
}

const gamepadListener = new GamepadListener();
const gamepadState = atom({
  key: 'gamepadIndex',
  default: { gamepadListener, capture: false },
  dangerouslyAllowMutability: true,
});

if (USE_VIRTUAL_GAMEPAD) {
  window.__SIRONA_GAMEPAD_ADAPTER__ = gamepadListener;
  new VirtualGamepad();
}

export const DEV_KEYMAPPING: DictaphoneKeyMapping = VIRTUAL_DICTAPHONE_DEFAULT_KEY_MAPPING;

export const getGamepadAction = (
  evt: { keyCode: string; pressed: boolean },
  keyMapping: DictaphoneKeyMapping = DEV_KEYMAPPING
): DictaphoneKey | undefined => {
  const id = keyMapping[evt.keyCode];
  if (id == null) {
    return undefined;
  }

  return { key: evt.keyCode, id };
};

const toKeyMappingRecord = (
  keyMapping: DictaphonePreferences['keyMapping'] | undefined
): DictaphoneKeyMapping | undefined => {
  if (keyMapping == null) {
    return undefined;
  }

  if (keyMapping.length === 0) {
    return undefined;
  }

  return Object.fromEntries(keyMapping.map(({ key, id }) => [key, id]));
};

const useEffectiveGamepadKeyMapping = ({ context }: WithApolloContext) => {
  const [toolPreferences] = useToolPreferences({ context });
  const { keyMapping: programmableMicKeyMapping } = useDictaphoneKeyMapping({ context });

  const [isProgrammableMicButtonsEnabled] = useFeatureFlagEnabled(
    FF.REPORTER_PROGRAMMABLE_MICROPHONE_BUTTONS
  );

  const legacyPreferenceKeyMapping = useMemo(
    () => toKeyMappingRecord(toolPreferences?.dictaphone.keyMapping),
    [toolPreferences?.dictaphone.keyMapping]
  );

  return useMemo(() => {
    const selectedKeyMapping = isProgrammableMicButtonsEnabled
      ? programmableMicKeyMapping
      : legacyPreferenceKeyMapping;

    // on local development builds, if the developer doesn't have some key mapping
    // set on their profile (or matching product), fallback to the config needed to make
    // the virtual gamepad work
    if (USE_VIRTUAL_GAMEPAD && selectedKeyMapping == null) {
      return DEV_KEYMAPPING;
    }

    return selectedKeyMapping;
  }, [isProgrammableMicButtonsEnabled, legacyPreferenceKeyMapping, programmableMicKeyMapping]);
};

export const useGamepad = ({
  callback,
  capture = false,
  context,
}: {
  callback: (arg1: { keyCode: string; pressed: boolean }) => void;
  capture?: boolean;
} & WithApolloContext) => {
  // Gamepad behavior should only be enabled if a dictaphone is the active input
  const { isDictaphone } = useMicrophone({ context });

  const [{ gamepadListener }, setGamepad] = useRecoilState(gamepadState);

  useEffect(() => {
    if (!capture) {
      return;
    }

    setGamepad((state) => ({ ...state, capture }));

    return () => {
      setGamepad((state) => ({ ...state, capture: false }));
    };
  }, [capture, setGamepad]);

  useEffect(() => {
    if (gamepadListener == null || !isDictaphone) {
      return;
    }

    const cb = ({
      detail,
    }: {
      detail: {
        pressed: boolean;
        button: string;
      };
    }) => {
      callback({ keyCode: String(detail.button), pressed: detail.pressed });
    };

    gamepadListener.start();
    gamepadListener.on('gamepad:button', cb);

    return () => {
      gamepadListener.off('gamepad:button', cb);
      // If nothing else is listening to the gamepad we can stop the listener
      // in order to speed up the whole app
      if (gamepadListener?.events != null && Object.keys(gamepadListener.events).length === 0) {
        gamepadListener.stop();
      }
    };
  }, [gamepadListener, capture, callback, setGamepad, isDictaphone]);
};

export type UseGamepadBindingsParams = WithApolloContext & {
  callback: (params: { action: GamepadActionId; pressed: boolean; keyCode?: string }) => void;
};

export const useGamepadBindings = ({ callback, context }: UseGamepadBindingsParams) => {
  const [{ capture }] = useRecoilState(gamepadState);
  const keyMapping = useEffectiveGamepadKeyMapping({ context });

  const gamepadCallback = useCallback(
    (evt: { keyCode: string; pressed: boolean }) => {
      if (capture) {
        return;
      }

      const action = getGamepadAction(evt, keyMapping);
      if (action != null) {
        callback({ action: action.id, pressed: evt.pressed, keyCode: evt.keyCode });
      }
    },
    [callback, capture, keyMapping]
  );

  useGamepad({ callback: gamepadCallback, context });
};
